import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { StatusDot, StatusGroup, DotStatus } from '../api'
import { PageDescription } from '../components/PageDescription'

// ── Dot colours ──────────────────────────────────────────────────────────────

function dotColour(status: DotStatus) {
    switch (status) {
        case 'Healthy':  return { ring: 'ring-emerald-500/40', bg: 'bg-emerald-500',    glow: 'shadow-emerald-500/30' }
        case 'Warning':  return { ring: 'ring-amber-400/40',   bg: 'bg-amber-400',      glow: 'shadow-amber-400/30'   }
        case 'Error':    return { ring: 'ring-red-500/40',     bg: 'bg-red-500',        glow: 'shadow-red-500/30'     }
        default:         return { ring: 'ring-slate-600/40',   bg: 'bg-slate-600',      glow: ''                      }
    }
}

function summaryBadge(dots: StatusDot[]) {
    const errors   = dots.filter(d => d.status === 'Error').length
    const warnings = dots.filter(d => d.status === 'Warning').length
    if (errors > 0) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-500/15 text-red-400 ring-1 ring-red-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                {errors} error{errors !== 1 ? 's' : ''}
            </span>
        )
    }
    if (warnings > 0) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                {warnings} warning{warnings !== 1 ? 's' : ''}
            </span>
        )
    }
    const unknowns = dots.filter(d => d.status === 'Unknown').length
    if (unknowns === dots.length) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-700/50 text-slate-500 ring-1 ring-slate-600/30">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
                Unknown
            </span>
        )
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Healthy
        </span>
    )
}

// ── Single status dot with hover popover ─────────────────────────────────────

function StatusDotItem({ dot, visible, delay }: { dot: StatusDot; visible: boolean; delay: number }) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const colour = dotColour(dot.status)

    return (
        <div
            ref={ref}
            className="flex flex-col items-center gap-2 relative"
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'scale(1)' : 'scale(0.8)',
                transition: `opacity 300ms ease ${delay}ms, transform 300ms ease ${delay}ms`,
            }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            {/* Dot */}
            <button
                className={`h-4 w-4 rounded-full ring-2 shadow-md transition-transform hover:scale-125 focus:outline-none ${colour.ring} ${colour.bg} ${colour.glow}`}
                aria-label={`${dot.label}: ${dot.status}`}
            />

            {/* Label — wrap on \n for multi-line (e.g. "tenant-api\ndev") */}
            <span className="text-[10px] text-slate-500 text-center leading-tight max-w-[60px] break-words whitespace-pre-wrap">
                {dot.label}
            </span>

            {/* Popover */}
            {open && dot.detail && (
                <div className="absolute bottom-full mb-2 z-50 pointer-events-none">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 shadow-xl text-xs text-slate-200 whitespace-nowrap max-w-xs">
                        <div className="font-semibold text-white mb-0.5">{dot.label.replace('\n', ' › ')}</div>
                        <div className="text-slate-400">{dot.detail}</div>
                    </div>
                    {/* Arrow */}
                    <div className="flex justify-center">
                        <div className="h-1.5 w-1.5 bg-slate-800 border-r border-b border-slate-700 rotate-45 -mt-1" />
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Swimlane ─────────────────────────────────────────────────────────────────

function Swimlane({ group, visible, laneDelay }: { group: StatusGroup; visible: boolean; laneDelay: number }) {
    return (
        <section
            className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden"
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 400ms ease ${laneDelay}ms, transform 400ms ease ${laneDelay}ms`,
            }}
        >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-900/80">
                <span className="text-sm font-semibold text-white">{group.label}</span>
                {summaryBadge(group.dots)}
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-5 px-4 sm:px-6 py-5">
                {group.dots.map((dot, i) => (
                    <StatusDotItem
                        key={dot.id}
                        dot={dot}
                        visible={visible}
                        delay={laneDelay + i * 40}
                    />
                ))}
                {group.dots.length === 0 && (
                    <span className="text-xs text-slate-600">No data</span>
                )}
            </div>
        </section>
    )
}

// ── Legend ────────────────────────────────────────────────────────────────────

function Legend() {
    const items: { status: DotStatus; label: string }[] = [
        { status: 'Healthy', label: 'Healthy' },
        { status: 'Warning', label: 'Warning' },
        { status: 'Error',   label: 'Error'   },
        { status: 'Unknown', label: 'Unknown' },
    ]
    return (
        <div className="flex items-center gap-5">
            {items.map(({ status, label }) => {
                const c = dotColour(status)
                return (
                    <div key={status} className="flex items-center gap-1.5">
                        <span className={`h-3 w-3 rounded-full ring-2 ${c.ring} ${c.bg}`} />
                        <span className="text-xs text-slate-500">{label}</span>
                    </div>
                )
            })}
        </div>
    )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function StatusPage() {
    const [visible, setVisible] = useState(false)

    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['infra-status'],
        queryFn: releaseApi.getInfraStatus,
        refetchInterval: 30_000,
    })

    useEffect(() => {
        if (data) {
            const t = setTimeout(() => setVisible(true), 60)
            return () => clearTimeout(t)
        }
        return undefined
    }, [data])

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Infra Status</span>
                        <PageDescription subtitle="GitOps · Cluster · Ingress · Data · Services" storageKey="desc-status" detail="Rolled-up infrastructure status across all signal sources. Red means at least one component in that category is degraded. Click any dot to jump to the relevant detail page. This is the same data the oncall team watches." />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-8">
                <div className="max-w-5xl mx-auto">
                    {isLoading && (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-slate-500 text-sm animate-pulse">Fetching infra status…</div>
                        </div>
                    )}

                    {isError && (
                        <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm space-y-1">
                            <p className="font-medium">Failed to load infra status</p>
                            {error instanceof Error && (
                                <p className="font-mono text-xs text-red-400 break-all">{error.message}</p>
                            )}
                        </div>
                    )}

                    {data && (
                        <>
                            <div className="mb-6 flex items-center justify-between">
                                <Legend />
                                <span className="text-[10px] text-slate-600 font-mono">
                                    Hover a dot for detail
                                </span>
                            </div>
                            <div className="space-y-4">
                                {data.groups.map((group: StatusGroup, i: number) => (
                                    <Swimlane
                                        key={group.id}
                                        group={group}
                                        visible={visible}
                                        laneDelay={i * 80}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    )
}
